import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import type { MouseEvent, ReactElement } from "react";

import {
  Box,
  ButtonBase,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListSubheader,
  TextField,
} from "@mui/material";
import { amber, blueGrey, grey, lightBlue } from "@mui/material/colors";
import { IconStar, IconPencil } from "@tabler/icons-react";
import { Capacitor } from "@capacitor/core";
import { PrivacyScreen } from "@capacitor/privacy-screen";

import PhotoDetails from "features/photos/PhotoDetails";
import StorageImage from "components/StorageImage";
import CurrentSession from "data/userSession";
import Image from "data/firestore/image";
import TimeUtils from "utils/time";
import { useCustomSnackbar } from "hooks/customSnackbar";

const COLS = 3;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ImageType = any;

// Instagram-style gallery: square thumbnails, 3 per row, newest first. Clicking a
// thumbnail expands a full-width detail drawer below its row, with an arrow pointing
// up at the clicked tile. Reuses ImageTile for the details/edit/favorite/delete logic.
const PhotoGrid = ({
  images,
  editable = false,
  onChange = () => {},
  focusId = null,
  showDates = true,
}: {
  images: ImageType[];
  editable?: boolean;
  onChange?: () => void;
  focusId?: string | null;
  showDates?: boolean;
}) => {
  // openId = which drawer is expanded; mountedId = which is still in the DOM (kept
  // through the collapse exit so closing animates too).
  const [openId, setOpenId] = useState<string | null>(null);
  const [mountedId, setMountedId] = useState<string | null>(null);
  const [loggedIn, setLoggedIn] = useState(false);
  // Album being renamed (null = dialog closed) + the in-progress new name.
  const [renaming, setRenaming] = useState<string | null>(null);
  const [newAlbumName, setNewAlbumName] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  const tileRefs = useRef<Record<string, HTMLElement | null>>({});
  const { enqueueSnackbar } = useCustomSnackbar();

  useEffect(() => {
    CurrentSession.load().then(() => setLoggedIn(CurrentSession.isSignedIn));
  }, []);

  // A freshly uploaded photo (focusId): expand it and scroll it into view so the
  // user can start editing right away.
  useEffect(() => {
    if (!focusId) return;
    setMountedId(focusId);
    setOpenId(focusId);
    requestAnimationFrame(() => {
      tileRefs.current[focusId]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [focusId]);

  const toggle = (id: string) => {
    if (openId === id) {
      setOpenId(null); // start close; mountedId cleared on exit
    } else {
      setMountedId(id);
      setOpenId(id);
    }
  };

  // Block screenshots when viewing another user's (public) photos, mirroring ImageTile.
  const isOtherUsersImage =
    loggedIn &&
    images[0] &&
    !CurrentSession.authenticationStrictlyMatches(images[0].uid);

  useEffect(() => {
    if (Capacitor.isNativePlatform() && isOtherUsersImage) {
      PrivacyScreen.enable();
      return () => {
        PrivacyScreen.disable();
      };
    }
  }, [isOtherUsersImage]);

  // White drawer on the photos page (editable); light blue on Progress. The
  // up-arrow matches, plus a shadow so the white panel reads against the page.
  const panelBg = editable ? "#ffffff" : lightBlue[100];

  const albums = _.groupBy(images, "album");
  const albumNames = _.sortBy(_.keys(albums), (a) => a.toLowerCase());

  const startRename = (album: string) => {
    setRenaming(album);
    setNewAlbumName(album);
  };

  const confirmRename = async () => {
    if (!renaming) return;
    const target = newAlbumName.trim();
    if (!target || target === renaming) {
      setRenaming(null);
      return;
    }
    setSavingRename(true);
    try {
      const uid = albums[renaming]?.[0]?.uid ?? CurrentSession.user?.uid;
      const count = await Image.renameAlbum(uid, renaming, target);
      enqueueSnackbar(`Renamed album to "${target}" (${count} photos)`, {
        variant: "success",
      });
      setRenaming(null);
      onChange();
    } catch (e) {
      enqueueSnackbar((e as Error).message || "Failed to rename album", {
        variant: "error",
      });
    } finally {
      setSavingRename(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {albumNames.map((album) => {
        const sorted = _.orderBy(
          albums[album],
          (i: ImageType) => i.date,
          "desc"
        );
        const mountedIdx = sorted.findIndex(
          (i: ImageType) => i.id === mountedId
        );
        // Drawer appears after the last thumbnail in the mounted image's row.
        const drawerAfter =
          mountedIdx >= 0
            ? Math.min(
                Math.floor(mountedIdx / COLS) * COLS + COLS - 1,
                sorted.length - 1
              )
            : -1;
        const mounted = mountedIdx >= 0 ? sorted[mountedIdx] : null;
        const arrowLeft = `${(((mountedIdx % COLS) + 0.5) / COLS) * 100}%`;

        const cells: ReactElement[] = [];
        sorted.forEach((image: ImageType, idx: number) => {
          const isOpen = image.id === openId;
          cells.push(
            <Box
              key={image.id}
              ref={(el: HTMLElement | null) => {
                tileRefs.current[image.id] = el;
              }}
              onClick={() => toggle(image.id)}
              sx={{
                position: "relative",
                aspectRatio: "1",
                cursor: "pointer",
                overflow: "hidden",
                outline: isOpen ? `3px solid ${amber[500]}` : "none",
                outlineOffset: "-3px",
                zIndex: isOpen ? 1 : 0,
              }}
            >
              <StorageImage
                path={image.location}
                alt={image.caption || album}
                draggable={false}
                onContextMenu={(e: MouseEvent) => e.preventDefault()}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              {editable && showDates && image.favorited && (
                <IconStar
                  size={16}
                  color={blueGrey[900]}
                  fill={amber[500]}
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))",
                  }}
                />
              )}
              {showDates && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    px: 0.5,
                    py: "1px",
                    fontSize: "0.7rem",
                    color: grey[100],
                    backgroundColor: "rgba(0,0,0,0.5)",
                    borderBottomRightRadius: 4,
                  }}
                >
                  {TimeUtils.toMoment(image.date)?.format(
                    TimeUtils.FullDateString
                  )}
                </Box>
              )}
            </Box>
          );
          if (idx === drawerAfter && mounted) {
            cells.push(
              <Box key={`drawer-${mounted.id}`} sx={{ gridColumn: "1 / -1" }}>
                <Collapse
                  in={openId === mounted.id}
                  appear
                  timeout={300}
                  onExited={() =>
                    setMountedId((m) => (m === mounted.id ? null : m))
                  }
                >
                  <Box
                    sx={{
                      position: "relative",
                      my: "8px",
                      py: 2,
                      backgroundColor: panelBg,
                      border: `1px solid ${blueGrey[100]}`,
                      borderRadius: 2,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {/* arrow pointing up at the clicked tile */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: "-8px",
                        left: arrowLeft,
                        transform: "translateX(-50%)",
                        width: 0,
                        height: 0,
                        borderLeft: "8px solid transparent",
                        borderRight: "8px solid transparent",
                        borderBottom: `8px solid ${panelBg}`,
                        filter: `drop-shadow(0 -1px 0 ${blueGrey[100]})`,
                      }}
                    />
                    <PhotoDetails
                      image={mounted}
                      editable={editable}
                      onChange={onChange}
                    />
                  </Box>
                </Collapse>
              </Box>
            );
          }
        });

        return (
          <Box key={album} sx={{ mb: 3 }}>
            <ListSubheader
              disableSticky
              component="div"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "transparent",
                color: blueGrey[700],
                fontSize: "1.1rem",
                fontWeight: "bold",
                lineHeight: 2.5,
                px: 0.5,
              }}
            >
              {editable ? (
                <ButtonBase
                  onClick={() => startRename(album)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    borderRadius: 1,
                    px: 0.5,
                    font: "inherit",
                  }}
                >
                  <span>{album}</span>
                  <IconPencil size={16} />
                </ButtonBase>
              ) : (
                <span>{album}</span>
              )}
              <span style={{ color: blueGrey[400], fontWeight: 400 }}>
                {sorted.length.toLocaleString()}
              </span>
            </ListSubheader>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(${COLS}, 1fr)`,
                gap: "3px",
              }}
            >
              {cells}
            </Box>
          </Box>
        );
      })}

      <Dialog
        open={renaming !== null}
        onClose={() => !savingRename && setRenaming(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Rename album</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Album name"
            value={newAlbumName}
            onChange={(e) => setNewAlbumName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmRename();
            }}
            disabled={savingRename}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(null)} disabled={savingRename}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmRename}
            disabled={savingRename || !newAlbumName.trim()}
          >
            {savingRename ? "Renaming…" : "Rename"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhotoGrid;
